<template>
  <div class="calligraphy-canvas-container" @click="handleOutsideClick">
    <!-- 左侧组件与配置面板 -->
    <div class="config-panel">
      <!-- 组件相关列（组件库和组件配置垂直排列） -->
      <div class="components-related-section">
        <!-- 组件选择栏 -->
        <div class="components-section">
          <h3>组件库</h3>
          <div class="components-grid">
            <el-tooltip v-for="component in availableComponents" 
              :key="component.name"
              class="component-item" effect="dark" :content="component.description" placement="top">
              
            <div @click="addComponentToCanvas(component.name)">
              <div class="component-icon">{{ component.icon }}</div>
              <div class="component-name">{{ component.displayName }}</div>
            </div>
            </el-tooltip>
          </div>
        </div>
        
        <!-- 组件配置面板或页面属性面板 -->
        <div class="component-config-section">
          <template v-if="selectedComponent">
            <h3>组件配置</h3>
            <div class="component-config-header">
              <h4>{{ getComponentConfig(selectedComponent)?.displayName || selectedComponent.componentName }}</h4>
              <button class="btn btn-danger" @click="deleteSelectedComponent">删除组件</button>
            </div>
            
            <!-- 组件属性配置 -->
              <div class="component-properties">
                <!-- 过滤掉不需要的配置项 -->
                <div 
                  v-for="(property, propKey) in filteredProperties" 
                  :key="propKey"
                  class="property-group"
                  :style="{
                    flex: property.width ? `0 0 ${property.width}` : '0 0 auto'
                  }"
                  v-if="property.display !== 'none'"
                >
                  <label :for="`prop-${propKey}`">{{ property.label }}：</label>
                  
                  <!-- 文本输入 -->
                  <input 
                    v-if="property.type === 'text'"
                    :id="`prop-${propKey}`"
                    type="text"
                    v-model="selectedComponent.props[propKey]"
                    @blur="updateComponent(propKey)"
                  >
                  <el-input
                    v-if="property.type === 'textarea'"
                    :id="`prop-${propKey}`"
                    type="textarea"
                    :rows="2"
                    v-model="selectedComponent.props[propKey]"
                    @blur="updateComponent(propKey)">
                  </el-input>
                  
                  <!-- 数字输入 -->
                  <el-input-number 
                    v-else-if="property.type === 'number'"
                    :id="`prop-${propKey}`"
                    type="number"
                    :min="property.min"
                    :max="property.max"
                    v-model="selectedComponent.props[propKey]"
                    @blur="updateComponent(propKey)"
                    @change="updateComponent(propKey)"
                    size="small"
                    style="width: 100%;"
                  ></el-input-number>
                  
                  
                  <!-- 选择框 -->
                  <select 
                    v-else-if="property.type === 'select'"
                    :id="`prop-${propKey}`"
                    v-model="selectedComponent.props[propKey]"
                    @change="updateComponent(propKey)"
                  >
                    <option 
                      v-for="option in property.options" 
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </select>
                  
                  <!-- 颜色选择器 -->
                  <input 
                    v-else-if="property.type === 'color'"
                    :id="`prop-${propKey}`"
                    type="color"
                    v-model="selectedComponent.props[propKey]"
                    @input="updateComponent(propKey)"
                    style="width: 100%;"
                  >

                  <!-- 开关 -->
                  <el-switch
                    v-else-if="property.type === 'boolean'"
                    v-model="selectedComponent.props[propKey]"
                    @change="updateComponent(propKey)"
                  ></el-switch>
                  
                  <!-- 多选框（array类型） -->
                  <el-checkbox-group 
                    v-else-if="property.type === 'array'"
                    v-model="selectedComponent.props[propKey]"
                    @change="updateComponent(propKey)"
                  >
                    <el-checkbox v-for="option in property.options" :key="option.value" :label="option.value">{{ option.label }}</el-checkbox>
                  </el-checkbox-group>
                </div>
              </div>
          </template>
          
          <!-- 页面属性面板 -->
          <template v-else>
            <h3>页面属性</h3>
            <div class="component-properties">
              <h4 style="width: 100%;">页面内边距</h4>
              <!-- 上内边距 -->
              <div class="property-group">
                <label for="page-padding-top">上内边距：</label>
                <input 
                  id="page-padding-top"
                  type="number"
                  min="0"
                  max="100"
                  v-model.number="pageProperties.paddingTop"
                  @input="updatePageProperties"
                >
              </div>
              <!-- 下内边距 -->
              <div class="property-group">
                <label for="page-padding-bottom">下内边距：</label>
                <input 
                  id="page-padding-bottom"
                  type="number"
                  min="0"
                  max="100"
                  v-model.number="pageProperties.paddingBottom"
                  @input="updatePageProperties"
                >
              </div>
              <!-- 左内边距 -->
              <div class="property-group">
                <label for="page-padding-left">左内边距：</label>
                <input 
                  id="page-padding-left"
                  type="number"
                  min="0"
                  max="100"
                  v-model.number="pageProperties.paddingLeft"
                  @input="updatePageProperties"
                >
              </div>
              <!-- 右内边距 -->
              <div class="property-group">
                <label for="page-padding-right">右内边距：</label>
                <input 
                  id="page-padding-right"
                  type="number"
                  min="0"
                  max="100"
                  v-model.number="pageProperties.paddingRight"
                  @input="updatePageProperties"
                >
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
    
    <!-- 右侧主要内容区域：画布 + 组件层 -->
    <div class="main-content-section">
      <!-- 画布区域 -->
      <div class="canvas-section">
        <div class="canvas-header">
          <h4>绘制区域</h4>
          <div class="canvas-controls">
            <div class="canvas-info">
              <span>{{ canvasWidth }} × {{ canvasHeight }} px</span>
            </div>
            <div class="zoom-control">
              <label for="paperSize">纸张大小：</label>
              <el-select id="paperSize" v-model="paperSize" @change="updateCanvasSize" size="small">
                <el-option label="A4 (210×297mm)" value="a4"></el-option>
                <el-option label="A3 (297×420mm)" value="a3"></el-option>
                <el-option label="A5 (148×210mm)" value="a5"></el-option>
              </el-select>
              <label for="zoom">缩放：</label>
              <el-select id="zoom" v-model="zoomLevel" @change="updateZoom" size="small" style="width: 100px;"> 
                <el-option label="自适应" value="auto"></el-option>
                <el-option label="50%" value="0.5"></el-option>
                <el-option label="75%" value="0.75"></el-option>
                <el-option label="100%" value="1"></el-option>
                <el-option label="125%" value="1.25"></el-option>
                <el-option label="150%" value="1.5"></el-option>
                <el-option label="200%" value="2"></el-option>
              </el-select>
            </div>
            <div class="resolution-control">
              <label for="resolution">分辨率：</label>
              <el-select id="resolution" v-model="saveResolution" size="small" style="width: 100px;">
                <el-option label="1x (默认)" :value="1"></el-option>
                <el-option label="2x (高清)" :value="2"></el-option>
                <el-option label="3x (超清)" :value="3"></el-option>
                <el-option label="4x (超高清)" :value="4"></el-option>
              </el-select>
            </div>
            <div class="save-control">
              <el-button size="small" type="primary" @click="saveCanvas">保存</el-button>
            </div>
          </div>
        </div>
        <div class="canvas-wrapper">
          <div
              ref="canvasContainer"
              class="canvas-container"
              :style="{
                width: `${canvasWidth}px`,
                height: `${canvasHeight}px`,
                transform: `scale(${calculatedZoom})`,
                transformOrigin: 'top left',
                userSelect: 'none',
                WebkitUserSelect: 'none',
                MozUserSelect: 'none',
                MsUserSelect: 'none'
              }"
              @click="clearSelection"
            >
            <!-- 组件渲染区域 -->
            <div 
              class="canvas-components-container"
              :style="{
                padding: `${pageProperties.paddingTop}px ${pageProperties.paddingRight}px ${pageProperties.paddingBottom}px ${pageProperties.paddingLeft}px`,
                boxSizing: 'border-box',
                overflow: 'visible'
              }"
            >
              <div
                v-for="(component, index) in componentInstances"
                :key="component.id"
                class="canvas-component-wrapper"
                :id="component.id"
                :style="{
                  zIndex: 1,
                  boxSizing: 'border-box',
                  display: component.props.display === 'block' ? 'block' : 'inline-block',
                  width: component.props.display === 'block' ? '100%' : 'auto',
                  transition: 'all 0.3s ease',
                  overflow: 'visible',
                  verticalAlign: 'top'
                }"
                :class="{
                  'component-dragging': isDragging && draggedComponent?.id === component.id,
                  'component-drag-over': dragOverComponent?.id === component.id
                }"
              >
                <!-- 选择状态边框容器 -->
                <div
                  :class="['canvas-component', {
                    'component-selected': component.selected,
                    'component-block': component.props.display === 'block',
                    'component-inline': component.props.display === 'inline-block'
                  }]"
                  :style="{
                    width: component.props.display === 'block' ? '100%' : (component.componentName === 'container' ? `${component.props.width || 377}px` : '100%'),
                    minHeight: component.componentName === 'container' ? `${component.props.height || 100}px` : `${component.height + 'px'}`,
                    backgroundColor: getComponentStyle(component).backgroundColor,
                    position: 'relative',
                    display: component.props.display || 'block',
                    marginTop: component.props.marginTop + 'px',
                    marginBottom: component.props.marginBottom + 'px',
                    marginLeft: component.props.marginLeft + 'px',
                    marginRight: component.props.marginRight + 'px',
                    boxSizing: 'border-box',
                    maxWidth: '100%'
                  }"
                  @click.stop="selectComponent(component.id)"
                >
                  <!-- 拖拽移动标识图标 -->
                  <div
                    v-if="component.selected"
                    class="component-move-handle"
                    :style="{
                      position: 'absolute',
                      top: '-10px',
                      right: '-10px',
                      width: '24px',
                      height: '24px',
                      backgroundColor: '#4CAF50',
                      borderRadius: '50%',
                      cursor: 'move',
                      zIndex: 100,
                      boxShadow: '0 2px 5px rgba(0, 0, 0, 0.2)',
                      overflow: 'visible',
                      pointerEvents: 'all'
                    }"
                    @click.stop
                  >
                    <div 
                      :style="{
                        position: 'absolute',
                        top: '0',
                        left: '0',
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        fontSize: '16px',
                        color: 'white',
                        pointerEvents: 'none'
                      }"
                    >
                      ☰
                    </div>
                  </div>
                  
                  <!-- 动态渲染非容器组件 -->
                  <div class="dynamic-component-content" v-if="component.componentName !== 'container'" v-html="renderComponent(component)" @click="handleComponentClick($event, component)"></div>
                  
                  <!-- 容器组件子组件渲染 -->
                  <template v-else>
                    <div class="container-children-wrapper" 
                      :style="{
                        padding: `${component.props.paddingTop}px ${component.props.paddingRight}px ${component.props.paddingBottom}px ${component.props.paddingLeft}px`,
                        boxSizing: 'border-box',
                        width: '100%',
                        minHeight: `${component.props.height || 100}px`,
                        overflow: 'visible'
                      }"
                    >
                      <div
                        v-if="component.children.length == 0"
                        :style="{
                          width: '100%',
                          height: '100%',
                          background: '#DFDFDF',
                          minHeight: `${component.props.height || 100}px`,
                          textAlign: 'center',
                          lineHeight: `${component.props.height || 100}px`
                        }"
                      >
                        {{ getComponentConfig(component)?.displayName || component.componentName }}
                      </div>
                          <div
                            v-else
                            v-for="(childComponent, childIndex) in component.children"
                            :key="childComponent.id"
                            :id="childComponent.id"
                            class="canvas-component-wrapper child-component"
                            :style="{
                              zIndex: 2,
                              boxSizing: 'border-box',
                              display: childComponent.props.display === 'block' ? 'block' : 'inline-block',
                              width: childComponent.props.display === 'block' ? '100%' : 'auto',
                              transition: 'all 0.3s ease',
                              overflow: 'visible',
                              verticalAlign: 'top',
                              margin: `${childComponent.props.marginTop}px ${childComponent.props.marginRight}px ${childComponent.props.marginBottom}px ${childComponent.props.marginLeft}px`
                            }"
                            :class="{
                              'component-dragging': isDragging && draggedComponent?.id === childComponent.id,
                              'component-drag-over': dragOverComponent?.id === childComponent.id
                            }"
                            draggable="true"
                            @dragover="onDragOver($event, childComponent, childIndex, component)"
                            @dragenter="onDragEnter($event, childComponent, childIndex, component)"
                            @dragleave="onDragLeave($event, childComponent, childIndex, component)"
                            @drop="onDrop($event, childComponent, childIndex, component)"
                            @dragend="onDragEnd($event, childComponent, childIndex, component)"
                          >
                            <!-- 子组件选择状态边框容器 -->
                            <div
                              :class="['canvas-component', {
                                'component-selected': childComponent.selected,
                                'component-block': childComponent.props.display === 'block',
                                'component-inline': childComponent.props.display === 'inline-block'
                              }]"
                              :style="{
                                width: childComponent.props.display === 'block' ? '100%' : (childComponent.componentName === 'container' ? `${childComponent.props.width || 377}px` : '100%'),
                                height: childComponent.height + childComponent.props.paddingTop + childComponent.props.paddingBottom + 'px',
                                backgroundColor: getComponentStyle(childComponent).backgroundColor,
                                position: 'relative',
                                display: childComponent.props.display || 'block',
                                marginTop: component.props.marginTop + 'px',
                                marginBottom: component.props.marginBottom + 'px',
                                marginLeft: component.props.marginLeft + 'px',
                                marginRight: component.props.marginRight + 'px',
                                boxSizing: 'border-box'
                              }"
                              @click.stop="selectComponent(childComponent.id)"
                            >
                              <!-- 子组件拖拽移动标识图标 -->
                              <div
                                v-if="childComponent.selected"
                                class="component-move-handle"
                                :style="{
                                  position: 'absolute',
                                  top: '-10px',
                                  right: '-10px',
                                  width: '24px',
                                  height: '24px',
                                  backgroundColor: '#4CAF50',
                                  borderRadius: '50%',
                                  cursor: 'move',
                                  zIndex: 1001,
                                  boxShadow: '0 2px 5px rgba(0, 0, 0, 0.2)',
                                  overflow: 'visible',
                                  pointerEvents: 'all'
                                }"
                                draggable="true"
                                @dragstart="onDragStart($event, childComponent, childIndex, component)"
                                @click.stop
                              >
                              </div>
                              
                              <!-- 动态渲染子组件 -->
                              <div class="dynamic-component-content" v-html="renderComponent(childComponent)"></div>
                            </div>
                          </div>
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 组件层区域 -->
      <div class="components-layer-section">
        <div class="components-layer-header">
          <h4>组件层</h4>
          <div class="components-layer-actions">
            <el-button type="danger" size="small"
              :disabled="componentInstances.length === 0"
              @click="clearCanvas">清空</el-button>
            <button 
              class="btn btn-sm btn-secondary" 
              @click="undo"
              size="small"
              :disabled="historyIndex < 0"
              title="撤销"
            >
              ↶ 撤销
            </button>
          </div>
        </div>
        <div class="components-layer-content">
          <div v-if="componentInstances.length === 0" class="no-components-message">
            <span>暂无组件</span>
          </div>
          <div v-else class="components-list">
            <!-- 递归显示组件及其子组件 -->
            <div
              v-for="(component, index) in componentInstances"
              :key="component.id"
            >
              <div
                class="component-layer-item"
                :class="{
                  'component-layer-item-selected': component.selected,
                  'component-dragging': isDragging && draggedComponent?.id === component.id,
                  'component-drag-over': isDragging && (dragOverIndex === index || dragOverIndex === index + 1),
                  'bottom': isDragging && dragOverIndex === index + 1
                }"
                :draggable="true"
                @dragstart="onComponentLayerDragStart($event, component, index)"
                @dragover.prevent="onComponentLayerDragOver($event, index)"
                @drop="onComponentLayerDrop($event, index)"
                @click="selectComponent(component.id)">
                <div class="component-layer-item-drag-handle">
                ☰
              </div>
              <div class="component-layer-item-icon">
                {{ getComponentConfig(component)?.icon || '📦' }}
              </div>
              <div class="component-layer-item-info">
                <div class="component-layer-item-name">
                  {{ index + 1 }}. {{ getComponentConfig(component)?.displayName || component.componentName }}
                </div>
                <div class="component-layer-item-size">
                  {{ component.width || '100%' }} × {{ component.height }} px
                </div>
              </div>
              <div class="component-layer-item-actions">
                <button class="component-layer-item-delete" @click.stop="deleteComponent(component.id)">
                  ×
                </button>
              </div>
              </div>
            
            <!-- 递归显示子组件 -->
              <div
                v-for="(child, childIndex) in component.children"
                :key="child.id"
                class="component-layer-item component-layer-child-item"
                :class="{'component-layer-item-selected': child.selected, 'component-dragging': isDragging && draggedComponent?.id === child.id, 'component-drag-over': isDragging && (dragOverIndex === childIndex || dragOverIndex === childIndex + 1)}"
                :draggable="true"
                @click="selectComponent(child.id)"
                @dragstart.stop="onComponentLayerDragStart($event, child, childIndex, component)"
                @dragover.prevent.stop="onComponentLayerDragOver($event, childIndex, component)"
                @drop.stop="onComponentLayerDrop($event, childIndex, component)"
              >
                <div class="component-layer-item-icon">
                  {{ getComponentConfig(child)?.icon || '📦' }}
                </div>
                <div class="component-layer-item-info">
                  <div class="component-layer-item-name">
                    {{ index + 1 }}.{{ childIndex + 1 }}. {{ getComponentConfig(child)?.displayName || child.componentName }}
                  </div>
                  <div class="component-layer-item-size">
                    {{ child.width || '100%' }} × {{ child.height }} px
                  </div>
                </div>
                <div class="component-layer-item-select">
                  <input
                    type="radio"
                    :name="'component-select'"
                    :checked="child.selected"
                    @change="selectComponent(child.id)"
                  >
                </div>
                <div class="component-layer-item-actions">
                  <button class="component-layer-item-delete" @click.stop="deleteComponent(child.id)">
                    ×
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import html2canvas from 'html2canvas';
import ComponentManager from '../utils/ComponentManager';
import ComponentRenderer from '../utils/ComponentRenderer';
import ComponentSelectionMixin from '../mixins/ComponentSelectionMixin';

export default {
  name: 'CalligraphyCanvas',
  components: {
  },
  mixins: [ComponentSelectionMixin],
  data() {
    // 纸张大小选项 (像素值，基于96DPI)
    const paperSizes = {
      a4: { width: 794, height: 1123 }, // 210mm × 297mm at 96 DPI
      a3: { width: 1123, height: 1587 }, // 297mm × 420mm at 96 DPI
      a5: { width: 397, height: 562 }    // 148mm × 210mm at 96 DPI
    };
    
    // 默认纸张大小
    const defaultPaperSize = 'a4';
    
    return {
      // 纸张大小选项
      paperSizes,
      paperSize: defaultPaperSize, // 默认A4纸张
      canvasWidth: paperSizes[defaultPaperSize].width,
      canvasHeight: paperSizes[defaultPaperSize].height,
      // 拖拽相关
      isDragging: false,
      draggedComponent: null,
      draggedComponentParent: null,
      dragOverComponent: null,
      dragOverComponentParent: null,
      dragOffset: { x: 0, y: 0 },
      // 其他
      zoomLevel: 'auto', // 默认自适应缩放
      calculatedZoom: 1, // 计算出的实际缩放比例
      // 组件相关
      availableComponents: [], // 可用组件列表
      componentInstances: [], // 画布上的组件实例
      isComponentsLoading: true, // 组件库加载状态
      // 配置面板相关
      showBasicParams: false, // 基础参数默认折叠
      // 保存相关
      saveResolution: 1, // 保存分辨率，默认1x
      // 文档流相关
      documentFlow: {
        currentX: 0, // 当前文档流X位置，与画布顶点对齐，无默认边距
        currentY: 0, // 当前文档流Y位置，与画布顶点对齐，无默认边距
        lineHeight: 0  // 当前行高
      },
      // 页面属性
      pageProperties: {
        paddingTop: 20, // 页面上内边距
        paddingBottom: 20, // 页面下内边距
        paddingLeft: 20, // 页面左内边距
        paddingRight: 20 // 页面右内边距
      },
      // 组件层拖拽相关
      dragOverIndex: -1
    };
  },
  mounted() {
    // 初始化组件
    this.initComponents();
    // 初始化自适应缩放
    this.updateZoom();
    // 监听窗口大小变化
    window.addEventListener('resize', this.handleResize);
    
    // 确保在DOM渲染完成后重新计算缩放比例
    this.$nextTick(() => {
      this.updateZoom();

      // setTimeout(() => {
      //   this.addComponentToCanvas('hanzizitie');
      // }, 200);
    });
  },
  beforeDestroy() {
    // 移除事件监听
    window.removeEventListener('resize', this.handleResize);
  },
  computed: {
    // 过滤后的组件属性
    filteredProperties() {
      if (!this.selectedComponent) return {};
      
      const componentConfig = this.getComponentConfig(this.selectedComponent);
      if (!componentConfig || !componentConfig.properties) return {};
      
      const filtered = {};
      const excludedProps = ['contentType', 'totalCells', 'columns', 'borderWidth'];
      const componentProps = this.selectedComponent.props;
      
      Object.entries(componentConfig.properties).forEach(([propKey, property]) => {
        if (!excludedProps.includes(propKey)) {
          // 检查条件是否满足
          let conditionSatisfied = true;
          if (property.condition) {
            try {
              // 使用Function来计算条件表达式
              const conditionFn = new Function('props', `return ${property.condition}`);
              conditionSatisfied = conditionFn(componentProps);
            } catch (error) {
              console.error('条件计算错误:', error);
              conditionSatisfied = true; // 条件计算错误时默认显示
            }
          }
          
          if (conditionSatisfied) {
            // 复制属性并修改标签名
            const modifiedProperty = { ...property };
            if (propKey === 'strokeColor') {
              modifiedProperty.label = '边框颜色';
            }
            
            filtered[propKey] = modifiedProperty;
          }
        }
      });
      
      return filtered;
    },
    // 获取当前选中的组件
    selectedComponent() {
      // 递归查找选中的组件
      const findSelectedComponent = (components) => {
        for (const component of components) {
          if (component.selected) {
            return component;
          }
          if (component.children && component.children.length > 0) {
            const selectedChild = findSelectedComponent(component.children);
            if (selectedChild) {
              return selectedChild;
            }
          }
        }
        return null;
      };
      
      return findSelectedComponent(this.componentInstances);
    }
  },
  methods: {
    
    
    // 初始化组件
    async initComponents() {
      this.isComponentsLoading = true;
      try {
        await ComponentManager.loadComponents();
        this.availableComponents = ComponentManager.getAllComponents();
      } catch (error) {
        console.error('Failed to load components:', error);
      } finally {
        this.isComponentsLoading = false;
      }
    },
    // 更新Canvas大小
    updateCanvasSize() {
      const size = this.paperSizes[this.paperSize];
      if (size) {
        this.canvasWidth = size.width;
        this.canvasHeight = size.height;
        
        // 重置文档流位置
        this.resetDocumentFlow();
        
        // 重新排列所有组件位置
        this.reflowComponents();
        
        // 重新计算缩放比例
        this.updateZoom();
        
        console.log(`纸张大小已更新为 ${this.paperSize}: ${this.canvasWidth} × ${this.canvasHeight} px`);
      }
    },
    // 组件更新后触发重绘
    updateComponent(propKey) {

      if (this.selectedComponent) {
        const { properties, events } = this.selectedComponent;
        const propertie = properties[propKey];
        if (propertie && propertie.changeHandle && events && typeof events[propertie.changeHandle] === 'function') {
          events[propertie.changeHandle](this.selectedComponent, this);
        }
      }


      // 组件参数变化时，重新排列所有组件位置，避免重叠或留白
      this.reflowComponents();
    },
    // 清空组件选中状态和文字选中状态
    clearSelection() {
      // 递归清除所有组件的选中状态
      const clearAllSelections = (components) => {
        components.forEach(component => {
          component.selected = false;
          if (component.children && component.children.length > 0) {
            clearAllSelections(component.children);
          }
        });
      };
      
      // 清除所有组件的选中状态
      clearAllSelections(this.componentInstances);
      
      // 清除页面上的文字选中状态
      if (window.getSelection) {
        window.getSelection().removeAllRanges();
      } else if (document.selection) {
        document.selection.empty();
      }
    },
    // 获取组件配置信息
    getComponentConfig(componentInstance) {
      if (!componentInstance) return null;
      return ComponentManager.getComponent(componentInstance.componentName);
    },
    // 清空画布
    clearCanvas() {
      this.componentInstances = [];
      this.resetDocumentFlow();
    },
    
    // 处理点击画布外区域取消选中组件
    handleOutsideClick(event) {
      // 检查点击的元素是否是组件或组件相关区域
      const isComponent = event.target.closest('.canvas-component');
      const isConfigPanel = event.target.closest('.config-panel');
      const isComponentItem = event.target.closest('.component-item');
      const isComponentConfigSection = event.target.closest('.component-config-section');
      const isCanvasContainer = event.target.closest('.canvas-container');
      // 添加组件层区域检查
      const isComponentsLayerSection = event.target.closest('.components-layer-section');
      
      // 如果点击的是组件，不执行取消选中
      if (isComponent) {
        return;
      }
      
      // 如果点击的是组件层区域，不执行取消选中
      if (isComponentsLayerSection) {
        return;
      }
      
      // 如果点击的是画布容器内部，但不是组件，执行取消选中
      if (isCanvasContainer) {
        this.clearSelection();
        return;
      }
      
      // 如果点击的是配置面板相关区域，不执行取消选中
      if (isConfigPanel || isComponentItem || isComponentConfigSection) {
        return;
      }
      
      // 点击其他所有区域（画布外），取消选中组件
      this.clearSelection();
    },
    handleComponentClick(event, component) {
      console.log('点击了组件:', event, component);
      let dataset = null;

      if (event && event.target) {
        //循环往上查找元素中dataset存在handle的元素
        let currentElement = event.target;
        let i = 0;
        while (currentElement) {
          if (currentElement.classList.contains('dynamic-component-content')) {
            break;
          }
          dataset = currentElement.dataset || {};
          if (dataset.handle) {
            break;
          }
          currentElement = currentElement.parentElement;
          i++;
          if (i > 100) {
            break;
          }
        }
      }
      
      if (dataset === null) {
        return;
      }

      if (dataset && dataset.handle && component.events && typeof component.events[dataset.handle] === 'function') {
        let newDataset = {...dataset};
        delete newDataset['handle'];
        component.events[dataset.handle](component, newDataset, this);
      }
    },
    
    // 撤销操作
    undo() {
      if (this.historyIndex >= 0) {
        const historyItem = this.history[this.historyIndex];
        this.historyIndex--;
        this.componentInstances = JSON.parse(JSON.stringify(historyItem.componentInstances));
      }
    },
    
    // 组件层拖拽开始
    onComponentLayerDragStart(event, component, index, parentComponent = null) {
      this.isDragging = true;
      this.draggedComponent = component;
      this.draggedComponentParent = parentComponent;
      this.dragOverIndex = -1;
      event.dataTransfer.effectAllowed = 'move';
      // 设置拖拽数据，包含父组件信息
      event.dataTransfer.setData('text/plain', JSON.stringify({ index, parentId: parentComponent?.id || null }));
    },
    
    // 组件层拖拽经过
    onComponentLayerDragOver(event, index, parentComponent = null) {
      event.preventDefault();
      
      // 获取当前元素的位置信息
      const componentElement = event.currentTarget;
      const rect = componentElement.getBoundingClientRect();
      const elementCenter = rect.top + rect.height / 2;
      
      // 根据鼠标位置确定是插入到当前元素之前还是之后
      if (event.clientY < elementCenter) {
        // 鼠标在元素上方，插入到当前位置之前
        this.dragOverIndex = index;
      } else {
        // 鼠标在元素下方，插入到当前位置之后
        this.dragOverIndex = index + 1;
      }
      
      // 设置数据传输效果
      event.dataTransfer.dropEffect = 'move';
    },
    
    // 组件层拖拽放置
    onComponentLayerDrop(event, dropIndex, dropParentComponent = null) {
      if (this.draggedComponent && this.draggedComponent != dropParentComponent) {
        // 保存历史记录
        this.saveHistory('reorder');
        
        // 获取拖拽源数据
        const sourceData = JSON.parse(event.dataTransfer.getData('text/plain'));
        const sourceIndex = sourceData.index;
        const sourceParentId = sourceData.parentId;
      
        // 情况1：同一父容器内的拖拽
        if (sourceParentId === (dropParentComponent?.id || null)) {
          let components;
          let parentComponent = null;
          
          if (sourceParentId) {
            // 从父容器中获取子组件列表
            parentComponent = this.findComponentById(this.componentInstances, sourceParentId);
            components = parentComponent.children;
          } else {
            // 顶层组件列表
            components = this.componentInstances;
          }
          
          // 计算实际插入位置
          let actualDropIndex = this.dragOverIndex;
          
          // 如果拖拽元素从后面拖到前面，插入位置需要调整
          if (sourceIndex < actualDropIndex) {
            actualDropIndex--;
          }
          
          if (sourceIndex !== actualDropIndex) {
            // 移除拖拽组件
            const [draggedItem] = components.splice(sourceIndex, 1);
            // 插入到新位置
            components.splice(actualDropIndex, 0, draggedItem);
            
            // 如果是父容器内的拖拽，需要更新父容器的children属性
            if (parentComponent) {
              parentComponent.children = components;
            }
          }
        }
        // 情况2：不同容器间的拖拽
        else {
          let draggedItem;
          
          // 从源位置移除组件
          if (sourceParentId) {
            // 从父容器中移除子组件
            const sourceParent = this.findComponentById(this.componentInstances, sourceParentId);
            if (sourceParent) {
              [draggedItem] = sourceParent.children.splice(sourceIndex, 1);
            }
          } else {
            // 从顶层移除组件
            [draggedItem] = this.componentInstances.splice(sourceIndex, 1);
          }
          
          // 添加到目标位置
          if (dropParentComponent) {
            // 添加到父容器中
            if (!dropParentComponent.children) {
              dropParentComponent.children = [];
            }
            dropParentComponent.children.splice(this.dragOverIndex, 0, draggedItem);
          } else {
            // 添加到顶层
            this.componentInstances.splice(this.dragOverIndex, 0, draggedItem);
          }
        }
        
      }
    
      // 重置拖拽状态
      this.isDragging = false;
      this.draggedComponent = null;
      this.draggedComponentParent = null;
      this.dragOverIndex = -1;
    },
    // 重置文档流位置
    resetDocumentFlow() {
      this.documentFlow = {
        currentX: 0,
        currentY: 0,
        lineHeight: 0
      };
    },
    // 重新排列组件
    reflowComponents() {
      // 保存当前选中的组件ID
      const selectedComponentId = this.selectedComponent ? this.selectedComponent.id : null;
      
      // 清空组件实例
      const components = [...this.componentInstances];
      this.componentInstances = [];
      
      // 重置文档流
      this.resetDocumentFlow();
      
      // 重新添加组件，按照文档流排列
      components.forEach(component => {
        this.setComponentPosition(component);
        this.componentInstances.push(component);
      });
      
      // 重新选中组件
      if (selectedComponentId) {
        this.selectComponent(selectedComponentId);
      }
    },
    // 保存画布
    saveCanvas() {
      const canvasContainer = this.$refs.canvasContainer;
      if (!canvasContainer) {
        console.error('无法获取画布容器');
        return;
      }
      
      // 创建画布容器的克隆，避免影响原始显示
      const clonedContainer = canvasContainer.cloneNode(true);
      
      // 移除克隆容器中的事件监听器，避免不必要的事件触发
      clonedContainer.removeAttribute('@click');
      
      // 重置克隆容器的样式，确保使用原始尺寸
      clonedContainer.style.position = 'absolute';
      clonedContainer.style.top = '-9999px';
      clonedContainer.style.left = '-9999px';
      clonedContainer.style.transform = 'scale(1)';
      clonedContainer.style.transformOrigin = 'top left';
      clonedContainer.style.boxShadow = 'none';
      clonedContainer.style.width = `${this.canvasWidth}px`;
      clonedContainer.style.height = `${this.canvasHeight}px`;
      
      // 移除所有组件的选中状态样式，避免截图中显示选中边框
      const clonedComponents = clonedContainer.querySelectorAll('.canvas-component');
      clonedComponents.forEach(component => {
        component.classList.remove('component-selected');
        component.style.cursor = 'default';
      });
      
      // 将克隆容器添加到文档中
      document.body.appendChild(clonedContainer);
      
      // 使用html2canvas将克隆的画布内容转换为图片
      const resolution = parseInt(this.saveResolution, 10);
      html2canvas(clonedContainer, {
        scale: window.devicePixelRatio * resolution, // 使用设备像素比乘以选择的分辨率
        useCORS: true, // 允许加载跨域图片
        backgroundColor: '#ffffff', // 设置背景色为白色
        logging: false, // 禁用日志
        width: this.canvasWidth, // 明确指定宽度
        height: this.canvasHeight, // 明确指定高度
        ignoreElements: (element) => {
          // 忽略可能影响截图的元素
          return false;
        }
      }).then(canvas => {
        // 将canvas转换为图片数据URL
        const dataURL = canvas.toDataURL('image/png', 1.0);
        
        // 创建下载链接
        const link = document.createElement('a');
        link.href = dataURL;
        link.download = `canvas_${new Date().getTime()}_${resolution}x.png`;
        
        // 触发下载
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        console.log(`画布已保存为${resolution}x分辨率图片`);
      }).catch(error => {
        console.error('保存画布失败:', error);
      }).finally(() => {
        // 移除克隆容器
        document.body.removeChild(clonedContainer);
      });
    },
    // 处理窗口大小变化
    handleResize() {
      this.updateZoom();
    },
    // 更新缩放比例
    updateZoom() {
      if (this.zoomLevel === 'auto') {
        // 计算自适应缩放比例
        const containerWidth = this.$refs.canvasContainer?.parentElement?.clientWidth || 1000;
        const containerHeight = this.$refs.canvasContainer?.parentElement?.clientHeight || 1000;
        
        const scaleX = containerWidth / this.canvasWidth;
        const scaleY = containerHeight / this.canvasHeight;
        this.calculatedZoom = Math.min(scaleX, scaleY, 1); // 最大缩放比例为1
      } else {
        // 使用固定缩放比例
        this.calculatedZoom = parseFloat(this.zoomLevel);
      }
    },
    // 更新页面属性
    updatePageProperties() {
      // 页面属性更新后，重新排列所有组件位置
      this.reflowComponents();
    },
    // 渲染组件
    renderComponent(component) {
      return ComponentRenderer.renderComponent(component);
    }
  }
};
</script>

<style>
/* 全局样式 */
html, body {
  margin: 0;
  padding: 0;
  height: 100%;
  width: 100%;
}

/* 美化滚动条 */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #555;
}

/* 美化滚动条（Firefox） */
* {
  scrollbar-width: thin;
  scrollbar-color: #888 #f1f1f1;
}
</style>

<style scoped>
.calligraphy-canvas-container {
  display: flex;
  flex-direction: row;
  gap: 20px;
  padding: 0;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  background-color: #f8f9fa;
  overflow: hidden;
}

/* 左侧配置面板 */
  .config-panel {
    width: 290px;
    background-color: #ffffff;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    padding: 20px;
    overflow-y: auto;
    flex-shrink: 0;
    max-height: calc(100% - 20px);
    display: flex;
    flex-direction: row;
    gap: 20px;
  }
  
  /* 组件相关列（组件库和组件配置垂直排列） */
  .components-related-section {
    flex: 1;
    min-width: 250px;
    display: flex;
    flex-direction: column;
    gap: 20px;
    overflow-y: auto;
  }
  
  /* 组件选择栏 */
  .components-section {
    flex: 1;
  }
  
  /* 组件配置面板 */
  .component-config-section {
    flex: 3;
    background-color: #ffffff;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    padding: 20px;
    overflow-y: auto;
    margin-top: 20px;
  }

/* 右侧配置面板样式 */
  .config-section-right {
    width: 300px;
    background-color: #ffffff;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    padding: 20px;
    overflow-y: auto;
    flex-shrink: 0;
    max-height: calc(100vh - 40px);
  }
  
  .config-panel-right h3 {
    margin: 0 0 20px 0;
    color: #333;
    font-size: 18px;
    text-align: center;
    border-bottom: 1px solid #e0e0e0;
    padding-bottom: 10px;
  }
  
  .param-section {
    margin-bottom: 20px;
    padding-bottom: 15px;
    border-bottom: 1px solid #f0f0f0;
  }
  
  .param-section:last-child {
    border-bottom: none;
  }
  
  .param-section h4 {
    margin: 0 0 15px 0;
    color: #555;
    font-size: 14px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    padding: 5px 0;
  }
  
  .toggle-icon {
    font-size: 12px;
    color: #999;
  }
  
  .margin-controls {
    margin-bottom: 15px;
  }
  
  .margin-controls h5 {
    margin: 0 0 10px 0;
    color: #666;
    font-size: 13px;
    font-weight: 500;
  }
  
  .margin-row {
    display: flex;
    gap: 15px;
    margin-bottom: 10px;
  }
  
  /* 组件选择栏样式 */
  .components-section h3 {
    color: #333;
    font-size: 18px;
    text-align: center;
    border-bottom: 1px solid #e0e0e0;
    padding-bottom: 10px;
  }

.components-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  padding-top: 15px;
  overflow-y: auto;
}

.component-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px;
  background-color: #f8f9fa;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.component-item:hover {
  background-color: #e9ecef;
  border-color: #4CAF50;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.component-item:active {
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.component-icon {
  font-size: 16px;
  margin-bottom: 8px;
}

.component-name {
  font-size: 12px;
  font-weight: 500;
  color: #495057;
  text-align: center;
}

/* 配置部分样式 */
.config-section {
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #f0f0f0;
}

.config-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.config-section h4 {
  margin: 0 0 15px 0;
  color: #555;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.control-group {
  margin-bottom: 15px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.control-group label {
  font-weight: 500;
  color: #666;
  font-size: 14px;
}

.control-group input[type="text"],
.control-group input[type="number"],
.control-group select,
.control-group input[type="color"] {
  padding: 10px 14px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.3s ease;
  background-color: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.control-group input[type="text"]:hover,
.control-group input[type="number"]:hover,
.control-group select:hover,
.control-group input[type="color"]:hover {
  border-color: #4CAF50;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.control-group input[type="text"]:focus,
.control-group input[type="number"]:focus,
.control-group select:focus,
.control-group input[type="color"]:focus {
  outline: none;
  border-color: #4CAF50;
  box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.1);
}

/* 美化数字输入框的上下箭头 */
.control-group input[type="number"]::-webkit-inner-spin-button,
.control-group input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.control-group input[type="number"] {
  -moz-appearance: textfield;
}

/* 美化选择框 */
.control-group select {
  cursor: pointer;
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 16px;
  padding-right: 36px;
  appearance: none;
}

/* 美化颜色选择框 */
.control-group input[type="color"] {
  padding: 2px;
  height: 40px;
  cursor: pointer;
}

.control-group input[type="range"] {
  margin: 5px 0;
}

.control-group input[type="color"] {
  width: 100%;
  height: 40px;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  padding: 2px;
}

.control-group span {
  font-size: 12px;
  color: #888;
  text-align: right;
  margin-top: -5px;
}

.button-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.btn {
  padding: 10px 16px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  text-align: center;
}

.btn-primary {
  background-color: #4CAF50;
  color: white;
}

.btn-primary:hover {
    background-color: #45a049;
    transform: translateY(-1px);
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  }
  
  /* 删除按钮样式 */
  .btn-danger {
    background-color: #f44336;
    color: white;
  }
  
  .btn-danger:hover {
    background-color: #da190b;
    transform: translateY(-1px);
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  }

/* 主内容区域样式 */
.main-content-section {
  flex: 1;
  display: flex;
  flex-direction: row;
  gap: 20px;
  overflow: hidden;
}

/* Canvas区域样式 */
.canvas-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

/* 组件层区域样式 */
.components-layer-section {
  width: 250px;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex-shrink: 0;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

.components-layer-header {
  padding: 15px 20px;
  border-bottom: 1px solid #e0e0e0;
  background-color: #f8f9fa;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.components-layer-actions {
  display: flex;
  gap: 8px;
}

.btn {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s ease;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-sm {
  padding: 4px 8px;
  font-size: 12px;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #5a6268;
}

.components-layer-header h4 {
  margin: 0;
  color: #333;
  font-size: 16px;
}

.components-layer-content {
  flex: 1;
  padding: 10px;
  overflow-y: auto;
}

.no-components-message {
  text-align: center;
  color: #999;
  padding: 20px;
  font-size: 14px;
}

.components-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.component-layer-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: #ffffff;
  position: relative;
}

.component-layer-item-drag-handle {
  cursor: grab;
  font-size: 16px;
  color: #666;
  margin-right: 8px;
  user-select: none;
}

.component-layer-item-drag-handle:active {
  cursor: grabbing;
}

.component-layer-item-actions {
  margin-left: auto;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.component-layer-item:hover .component-layer-item-actions {
  opacity: 1;
}

.component-layer-item-delete {
  background-color: #ff4757;
  color: white;
  border: none;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.component-layer-item-delete:hover {
  background-color: #ff3742;
  transform: scale(1.1);
}

.component-dragging {
  opacity: 0.5;
  transform: rotate(5deg);
}

/* 插入线提示 */
.component-drag-over {
  position: relative;
}

.component-drag-over::before {
  content: '';
  position: absolute;
  top: -2px;
  left: 0;
  right: 0;
  height: 3px;
  background-color: #4CAF50;
  box-shadow: 0 0 8px rgba(76, 175, 80, 0.7);
  z-index: 10;
  animation: pulse 0.5s ease-in-out;
}

/* 当拖拽到末尾时，在底部显示插入线 */
.component-drag-over.bottom::before {
  top: auto;
  bottom: -2px;
}

/* 脉冲动画效果 */
@keyframes pulse {
  0% {
    transform: scaleX(0.8);
    opacity: 0.5;
  }
  50% {
    transform: scaleX(1);
    opacity: 1;
  }
  100% {
    transform: scaleX(0.8);
    opacity: 0.5;
  }
}

.component-layer-item:hover {
  border-color: #4CAF50;
  background-color: #f5f5f5;
}

.component-layer-item-selected {
  border-color: #4CAF50 !important;
  background-color: #e8f5e8 !important;
}

.component-layer-item-icon {
  font-size: 20px;
  width: 30px;
  text-align: center;
}

.component-layer-item-info {
  flex: 1;
  min-width: 0;
}

.component-layer-item-name {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.component-layer-item-size {
  font-size: 12px;
  color: #666;
}

.component-layer-item-select {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
}

.canvas-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #e0e0e0;
  background-color: #f8f9fa;
}

.canvas-header h4 {
  margin: 0;
  color: #333;
  font-size: 16px;
}

.canvas-controls {
  display: flex;
  align-items: center;
  gap: 15px;
}

.canvas-info {
  font-size: 14px;
  color: #666;
  background-color: #e9ecef;
  padding: 6px 12px;
  border-radius: 4px;
}

.zoom-control {
  display: flex;
  align-items: center;
  gap: 8px;
}

.zoom-control label {
  font-size: 14px;
  color: #666;
  font-weight: 500;
}

.zoom-control select {
  padding: 6px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
}

.resolution-control {
  display: flex;
  align-items: center;
  gap: 8px;
}

.resolution-control label {
  font-size: 14px;
  color: #666;
  font-weight: 500;
}

.resolution-control select {
  padding: 6px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
}

.save-control {
  margin-left: auto;
}

.canvas-wrapper {
  flex: 1;
  overflow: auto;
  padding: 20px;
  background-color: #f0f0f0;
  position: relative;
}

.canvas-container {
  background-color: white;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  position: relative;
  transition: transform 0.3s ease;
  overflow: hidden;
  box-sizing: content-box;
}

/* 画布组件样式 */
.canvas-component {
  position: absolute;
  box-sizing: border-box;
  cursor: pointer;
  transition: all 0.2s ease;
  overflow: visible;
}

.canvas-component:hover {
  box-shadow: 0 0 0 1px rgba(76, 175, 80, 0.5);
}

.component-selected {
  box-shadow: 0 0 0 2px #4CAF50 !important;
  z-index: 10;
}

/* 拖拽相关样式 */
.component-dragging {
  opacity: 0.5;
  transform: none;
  cursor: move;
}

.component-drag-over {
  box-shadow: 0 0 0 2px #2196F3 !important;
}

/* 预插入位置指示器 */
.insertion-indicator {
  position: absolute;
  left: 0;
  right: 0;
  height: 3px;
  background-color: #ff6b6b;
  box-shadow: 0 0 5px rgba(255, 107, 107, 0.8);
  z-index: 200;
  transition: all 0.2s ease;
}

/* 拖拽预览样式 */
.drag-preview {
  opacity: 0.8;
  background-color: rgba(76, 175, 80, 0.1);
  border: 1px solid #4CAF50;
  pointer-events: none;
  z-index: 1000;
}

/* 拖拽手柄样式 */
.component-move-handle {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  transition: all 0.2s ease;
}

.component-move-handle::after {
  content: '☰';
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  color: white;
  font-size: 16px;
  pointer-events: none;
}

.component-move-handle:hover {
  background-color: #45a049 !important;
  transform: scale(1.1);
}

.component-move-handle:active {
  transform: scale(0.95);
}

.component-block {
  display: block;
  width: 100%;
}

.component-inline {
  display: inline-block;
}

/* 组件配置面板标题 */
.component-config-section h3 {
  margin: 0 0 20px 0;
  color: #333;
  font-size: 18px;
  text-align: center;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 10px;
}

/* 组件配置头部 */
.component-config-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #f0f0f0;
}

.component-config-header h4 {
  margin: 0;
  color: #555;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* 组件属性组 */
.component-properties {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
}

/* 属性组样式 */
.property-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: calc(50% - 8px);
}

/* 属性标签样式 */
.property-group label {
  font-weight: 500;
  color: #666;
  font-size: 14px;
}

/* 属性输入框样式 */
.property-group input[type="text"],
.property-group input[type="number"],
.property-group select,
.property-group input[type="color"] {
  padding: 10px 14px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.3s ease;
  background-color: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

/* 输入框悬停样式 */
.property-group input[type="text"]:hover,
.property-group input[type="number"]:hover,
.property-group select:hover,
.property-group input[type="color"]:hover {
  border-color: #4CAF50;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* 输入框聚焦样式 */
.property-group input[type="text"]:focus,
.property-group input[type="number"]:focus,
.property-group select:focus,
.property-group input[type="color"]:focus {
  outline: none;
  border-color: #4CAF50;
  box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.1);
}

/* 美化选择框 */
.property-group select {
  cursor: pointer;
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 16px;
  padding-right: 36px;
  appearance: none;
}

/* 美化颜色选择框 */
.property-group input[type="color"] {
  padding: 2px;
  height: 40px;
  cursor: pointer;
}

/* 多选框样式 */
.multi-select-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.multi-select-options {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  max-height: 120px;
  overflow-y: auto;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background-color: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.multi-select-option {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  user-select: none;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background-color 0.2s ease;
}

.multi-select-option:hover {
  background-color: #f5f5f5;
}

.multi-select-option input[type="checkbox"] {
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #4CAF50;
}

.multi-select-option span {
  font-size: 14px;
  color: #666;
}

/* 标题组件样式 */
.title-container {
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  font-size: 32px;
  font-weight: bold;
  color: #333;
  font-family: SimHei, Microsoft YaHei;
}

/* 格子样式选择器样式 */
.grid-style-selector {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  margin-top: 10px;
}

.grid-style-option {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px;
  border: 2px solid #e0e0e0;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.grid-style-option:hover {
  border-color: #4CAF50;
  background-color: #f5f5f5;
}

.grid-style-option.active {
  border-color: #4CAF50;
  background-color: #e8f5e8;
}

.grid-style-preview {
  width: 40px;
  height: 40px;
  border: 1px solid #000;
  position: relative;
  margin-bottom: 5px;
}

.grid-style-preview span {
  font-size: 12px;
  color: #666;
}

/* 各种格子样式预览 */
.grid-style-preview.tian::before,
.grid-style-preview.tian::after {
  content: '';
  position: absolute;
  background-color: #000;
}

.grid-style-preview.tian::before {
  width: 100%;
  height: 1px;
  top: 50%;
  left: 0;
  transform: translateY(-50%);
}

.grid-style-preview.tian::after {
  height: 100%;
  width: 1px;
  left: 50%;
  top: 0;
  transform: translateX(-50%);
}

.grid-style-preview.mi::before,
.grid-style-preview.mi::after {
  content: '';
  position: absolute;
  background-color: #000;
}

.grid-style-preview.mi::before {
  width: 100%;
  height: 1px;
  top: 50%;
  left: 0;
  transform: translateY(-50%);
}

.grid-style-preview.mi::after {
  height: 100%;
  width: 1px;
  left: 50%;
  top: 0;
  transform: translateX(-50%);
}

.grid-style-preview.mi {
  background-image: 
    linear-gradient(45deg, transparent 48%, #000 48%, #000 52%, transparent 52%),
    linear-gradient(-45deg, transparent 48%, #000 48%, #000 52%, transparent 52%);
}

.grid-style-preview.hui::before {
  content: '';
  position: absolute;
  top: 25%;
  left: 25%;
  width: 50%;
  height: 50%;
  border: 1px solid #000;
}

.grid-style-preview.square {
  border: 1px solid #000;
}

.grid-style-preview.dotted {
  background-image: radial-gradient(circle, #000 1px, transparent 1px);
  background-size: 8px 8px;
}

.grid-style-preview.lined::before {
  content: '';
  position: absolute;
  width: 100%;
  height: 1px;
  top: 50%;
  left: 0;
  transform: translateY(-50%);
  background-color: #000;
}

.grid-style-preview.doubleLined::before,
.grid-style-preview.doubleLined::after {
  content: '';
  position: absolute;
  width: 100%;
  height: 1px;
  left: 0;
  background-color: #000;
}

.grid-style-preview.doubleLined::before {
  top: 33%;
}

.grid-style-preview.doubleLined::after {
  top: 66%;
}

.component-layer-child-item {
  margin-left: 20px;
}

</style>